import React, { useCallback } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import Saas404 from '../components/common/saas/Saas404';

function getPathForTenant(tenantName: string, path: string) {
  if (path.startsWith(tenantName)) {
    // already prefixed
    return path;
  }
  return `/${tenantName}${path}`;
}

// Helper hook for generating tenant-aware URLs
export const useTenantPath = () => {
  const { isAddEditHost, tenant } = useTenant();

  const getTenantRoute = useCallback(
    (path: string, element: React.ReactNode) => {
      // For addedit domain, we need both:
      // - The prefixed route (/tenant/path)
      // - A redirect from /path to /tenant/path
      if (isAddEditHost()) {
        const location = getPathForTenant(tenant, path);
        return (
          <>
            <Route key={`prefixed-${path}`} path={location} element={element} />
            <Route
              key={path}
              path={path}
              element={<Navigate to={location} replace />}
            />
          </>
        );
      }

      // For regular domains, just use the path as-is
      return <Route key={path} path={path} element={element} />;
    },
    [isAddEditHost, tenant],
  );

  const getPath = useCallback(
    (path: string) => {
      if (isAddEditHost()) {
        return getPathForTenant(tenant, path);
      }
      return path;
    },
    [isAddEditHost, tenant],
  );

  const getTenantLandingPage = useCallback(
    (fallback: React.ReactNode) => {
      if (isAddEditHost()) {
        /**
         * If a subscriber goes to https://addedit.mynextgensuite.com/ directly
         * for now, we will show a '404' -- eventually this will be a marketing
         * page or sign-up page
         */
        return <Saas404 target={'LANDING'} />;
      }
      return fallback;
    },
    [isAddEditHost],
  );

  const getTenantRootRoute = useCallback(() => {
    if (isAddEditHost()) {
      // For addedit domain, handle the root tenant path
      // e.g., /mlsnow should redirect to /mlsnow/create
      const tenantRoot = `/${tenant}`;
      const tenantCreate = `/${tenant}/create`;

      return (
        <Route
          key="tenant-root"
          path={tenantRoot}
          element={<Navigate to={tenantCreate} replace />}
        />
      );
    }
    // For regular domains, this isn't needed as "/" handles it
    return null;
  }, [isAddEditHost, tenant]);

  return {
    getPath,
    getTenantRoute,
    getTenantLandingPage,
    getTenantRootRoute,
  };
};
